import { distinctIdentityHandle } from "@/src/lib/identityPresentation";
import { Image } from "expo-image";
import { Ionicons } from "@/src/components/ui/ionicons";
import { InsetModal } from "@/src/components/ui/InsetModal";
import { Text } from "@/src/components/ui/BrandText";
import { accountCanBeSelected } from "@/src/lib/accountSelection";
import {
  accountDisplayName,
  accountPrimaryLabel,
} from "@/src/lib/accountPresentation";
import { avatarSourceFor } from "@/src/theme/defaultAvatars";
import { border, brand, surface, text as uiText } from "@/src/theme/tokens";
import type { AccountSummary } from "@/src/types/account";
import React, { useCallback, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  View,
} from "react-native";
import { useActiveAccount } from "@/src/hooks/useActiveAccount";

type AccountListProps = {
  accounts: AccountSummary[];
  activeAccountId: string | null;
  loading: boolean;
  error: unknown | null;
  onSelect: (accountId: string) => Promise<void>;
  onRefresh: () => void;
  compact?: boolean;
};

function AccountAvatar({ account, size }: { account: AccountSummary; size: number }) {
  const displayName = accountDisplayName(account);
  const source = avatarSourceFor(account.photoURL, account.accountId || displayName);

  return (
    <Image
      source={source}
      cachePolicy="disk"
      contentFit="cover"
      accessibilityLabel={`Avatar de ${displayName}`}
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        backgroundColor: surface.overlay,
        borderWidth: 1,
        borderColor: border.brand,
        flexShrink: 0,
      }}
    />
  );
}

function AccountRow({
  account,
  compact = false,
  selected = false,
}: {
  account: AccountSummary;
  compact?: boolean;
  selected?: boolean;
}) {
  const displayName = accountDisplayName(account);
  const badgeLabel = accountPrimaryLabel(account);
  const handle = distinctIdentityHandle(displayName, account.username);

  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: compact ? 10 : 12, flex: 1, minWidth: 0 }}>
      <AccountAvatar account={account} size={compact ? 42 : 50} />
      <View style={{ flex: 1, minWidth: 0, gap: 3 }}>
        <Text
          numberOfLines={1}
          ellipsizeMode="tail"
          style={{ color: uiText.primary, fontWeight: "900", fontSize: compact ? 15 : 16, flexShrink: 1, minWidth: 0 }}
        >
          {displayName}
        </Text>
        <Text numberOfLines={1} ellipsizeMode="tail" style={{ color: uiText.secondary, fontSize: 12, fontWeight: "700", flexShrink: 1, minWidth: 0 }}>
          {badgeLabel}
        </Text>
        {handle ? <Text numberOfLines={1} style={{ color: uiText.muted, fontSize: 12 }}>@{handle}</Text> : null}
      </View>
      {selected ? <Ionicons name="checkmark-circle" size={22} color={brand.primary} /> : null}
    </View>
  );
}

function AccountList({
  accounts,
  activeAccountId,
  loading,
  error,
  onSelect,
  onRefresh,
  compact = false,
}: AccountListProps) {
  if (loading) {
    return (
      <View style={{ minHeight: compact ? 52 : 72, alignItems: "center", justifyContent: "center" }}>
        <ActivityIndicator color={brand.primary} />
      </View>
    );
  }

  if (error) {
    return (
      <View style={{ gap: 8 }}>
        <Text style={{ color: uiText.muted, lineHeight: 19 }}>Les comptes liés sont momentanément indisponibles.</Text>
        <Pressable
          onPress={onRefresh}
          accessibilityRole="button"
          accessibilityLabel="Actualiser les comptes accessibles"
          style={{ alignSelf: "flex-start", minHeight: 44, justifyContent: "center" }}
        >
          <Text style={{ color: uiText.brand, fontWeight: "900" }}>Réessayer</Text>
        </Pressable>
      </View>
    );
  }

  if (accounts.length === 0) {
    return <Text style={{ color: uiText.muted }}>Aucun compte lié disponible.</Text>;
  }

  return (
    <View style={{ gap: compact ? 6 : 8 }}>
      {accounts.map((account) => (
        <AccountOption
          key={account.accountId}
          account={account}
          activeAccountId={activeAccountId}
          compact={compact}
          onSelect={onSelect}
        />
      ))}
    </View>
  );
}

function AccountOption({
  account,
  activeAccountId,
  compact = false,
  onSelect,
}: {
  account: AccountSummary;
  activeAccountId: string | null;
  compact?: boolean;
  onSelect: (accountId: string) => Promise<void>;
}) {
  const [focused, setFocused] = useState(false);
  const selected = account.accountId === activeAccountId;

  return (
    <Pressable
      onPress={() => void onSelect(account.accountId)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      accessibilityLabel={`Utiliser ${accountDisplayName(account)}`}
      style={({ pressed }) => ({
        minHeight: compact ? 64 : 76,
        padding: compact ? 9 : 11,
        borderRadius: 14,
        borderWidth: focused ? 2 : 1,
        borderColor: selected || focused ? brand.primary : border.subtle,
        backgroundColor: selected ? surface.selected : surface.primary,
        opacity: pressed ? 0.78 : 1,
      })}
    >
      <AccountRow account={account} compact={compact} selected={selected} />
    </Pressable>
  );
}

export function AccountSwitcher({
  label = "Identité active",
  description,
  compact = false,
  expanded = false,
  onAccountSelected,
}: {
  label?: string;
  description?: string;
  compact?: boolean;
  expanded?: boolean;
  onAccountSelected?: (accountId: string) => void;
}) {
  const {
    accounts,
    activeAccount,
    activeAccountId,
    loading,
    error,
    setActiveAccount,
    refresh,
  } = useActiveAccount();
  const [open, setOpen] = useState(false);
  const [triggerFocused, setTriggerFocused] = useState(false);
  const [closeFocused, setCloseFocused] = useState(false);
  const canSwitch = accounts.length > 1;
  const activeAccountLabel = activeAccount ? accountDisplayName(activeAccount) : "aucune identité";

  const selectAccount = useCallback(async (accountId: string) => {
    if (!accountCanBeSelected(accounts, accountId)) return;
    const selected = await setActiveAccount(accountId);
    if (!selected) return;
    setOpen(false);
    onAccountSelected?.(accountId);
  }, [accounts, onAccountSelected, setActiveAccount]);

  if (expanded) {
    return (
      <View style={{ borderRadius: 16, padding: 12, backgroundColor: surface.overlay, borderWidth: 1, borderColor: border.subtle, gap: 10 }}>
        <Text style={{ color: uiText.primary, fontWeight: "900", fontSize: 16 }}>{label}</Text>
        {description ? <Text style={{ color: uiText.muted, lineHeight: 20 }}>{description}</Text> : null}
        <AccountList
          accounts={accounts}
          activeAccountId={activeAccountId}
          loading={loading}
          error={error}
          onSelect={selectAccount}
          onRefresh={refresh}
        />
      </View>
    );
  }

  const trigger = activeAccount ? (
    <AccountRow account={activeAccount} compact={compact} />
  ) : loading ? (
    <View style={{ flex: 1, minHeight: 42, justifyContent: "center" }}>
      <Text style={{ color: uiText.muted, fontWeight: "800" }}>Chargement de l’identité...</Text>
    </View>
  ) : (
    <View style={{ flex: 1, minHeight: 42, justifyContent: "center" }}>
      <Text style={{ color: error ? uiText.danger : uiText.muted, fontWeight: "800" }}>
        {error ? "Identité publique indisponible" : "Aucune identité publique"}
      </Text>
    </View>
  );

  const triggerContent = (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 10, minWidth: 0 }}>
      {trigger}
      {canSwitch ? <Ionicons name="chevron-down" size={19} color={uiText.secondary} /> : null}
    </View>
  );

  return (
    <View style={{ gap: 6 }}>
      {label ? <Text style={{ color: uiText.muted, fontWeight: "800", fontSize: compact ? 12 : 13 }}>{label}</Text> : null}
      {canSwitch ? (
        <Pressable
          onPress={() => setOpen(true)}
          onFocus={() => setTriggerFocused(true)}
          onBlur={() => setTriggerFocused(false)}
          accessibilityRole="button"
          accessibilityLabel={`Changer d’identité publique. Actuellement ${activeAccountLabel}`}
          accessibilityHint="Ouvre la liste des comptes accessibles"
          style={({ pressed }) => ({
            borderRadius: 14,
            borderWidth: triggerFocused ? 2 : 1,
            borderColor: triggerFocused ? brand.primary : border.subtle,
            backgroundColor: surface.primary,
            padding: compact ? 9 : 11,
            opacity: pressed ? 0.8 : 1,
          })}
        >
          {triggerContent}
        </Pressable>
      ) : (
        <View style={{ borderRadius: 14, borderWidth: 1, borderColor: border.subtle, backgroundColor: surface.primary, padding: compact ? 9 : 11 }}>
          {triggerContent}
        </View>
      )}
      {description ? <Text style={{ color: uiText.muted, lineHeight: 19, fontSize: 12 }}>{description}</Text> : null}

      <InsetModal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}
      >
        <Pressable
          onPress={() => setOpen(false)}
          style={{ flex: 1, justifyContent: Platform.OS === "web" ? "center" : "flex-end", alignItems: "center" }}
          accessibilityLabel="Fermer le sélecteur de compte"
        >
          <View
            onStartShouldSetResponder={() => true}
            style={{
              width: "100%",
              maxWidth: 480,
              maxHeight: "88%",
              borderRadius: Platform.OS === "web" ? 22 : 22,
              borderBottomLeftRadius: Platform.OS === "web" ? 22 : 0,
              borderBottomRightRadius: Platform.OS === "web" ? 22 : 0,
              backgroundColor: surface.primary,
              borderWidth: 1,
              borderColor: border.subtle,
              padding: compact ? 16 : 18,
              gap: 12,
              shadowColor: "#000",
              shadowOpacity: 0.25,
              shadowRadius: 24,
              elevation: 12,
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={{ color: uiText.primary, fontSize: 20, fontWeight: "900" }}>Changer d’identité</Text>
              </View>
              <Pressable
                onPress={() => setOpen(false)}
                onFocus={() => setCloseFocused(true)}
                onBlur={() => setCloseFocused(false)}
                accessibilityRole="button"
                accessibilityLabel="Fermer le sélecteur d’identité"
                style={({ pressed }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: surface.overlay,
                  borderWidth: closeFocused ? 2 : 0,
                  borderColor: brand.primary,
                  opacity: pressed ? 0.78 : 1,
                })}
              >
                <Ionicons name="close" size={20} color={uiText.primary} />
              </Pressable>
            </View>
            <AccountList
              accounts={accounts}
              activeAccountId={activeAccountId}
              loading={loading}
              error={error}
              onSelect={selectAccount}
              onRefresh={refresh}
              compact={compact}
            />
          </View>
        </Pressable>
      </InsetModal>
    </View>
  );
}
